import {
  ALPHA_INVESTMENT_EXPERIENCE_CHOICES,
  ALPHA_RISK_TIME_HORIZON_CHOICES,
  isPersistedAlphaRiskScore,
  normalizeUsdAmount,
  type AlphaInvestmentExperience,
  type AlphaRiskProfileField,
  type SavedAlphaRiskProfileAnswers,
} from "./alphaRiskProfile";

const STORAGE_PREFIX = "plutus_alpha_risk_profile_draft";

const storageKey = (userId: string): string => `${STORAGE_PREFIX}:${userId}`;

export function loadAlphaRiskProfileDraft(
  userId: string | null
): SavedAlphaRiskProfileAnswers {
  if (!userId) return {};

  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};

    const draft: SavedAlphaRiskProfileAnswers = {};
    if (
      parsed.investmentTrack === "growth"
      || parsed.investmentTrack === "balance"
      || parsed.investmentTrack === "protect"
    ) draft.investmentTrack = parsed.investmentTrack;
    if (
      typeof parsed.plutusInvestment === "string"
      && normalizeUsdAmount(parsed.plutusInvestment)
    ) draft.plutusInvestment = normalizeUsdAmount(parsed.plutusInvestment) ?? undefined;
    if (
      typeof parsed.totalPortfolio === "string"
      && normalizeUsdAmount(parsed.totalPortfolio)
    ) draft.totalPortfolio = normalizeUsdAmount(parsed.totalPortfolio) ?? undefined;
    if (
      typeof parsed.plutusPercentage === "string"
      && /^(?:\d|[1-9]\d|100)(?:\.\d{1,2})?$/.test(parsed.plutusPercentage)
      && Number(parsed.plutusPercentage) <= 100
    ) draft.plutusPercentage = parsed.plutusPercentage;
    if (
      typeof parsed.investmentExperience === "string"
      && ALPHA_INVESTMENT_EXPERIENCE_CHOICES.some(
        (choice) => choice.value === parsed.investmentExperience
      )
    ) draft.investmentExperience = parsed.investmentExperience as AlphaInvestmentExperience;
    if (
      typeof parsed.timeHorizon === "string"
      && ALPHA_RISK_TIME_HORIZON_CHOICES.some(
        (choice) => choice.value === parsed.timeHorizon
      )
    ) draft.timeHorizon = parsed.timeHorizon as SavedAlphaRiskProfileAnswers["timeHorizon"];
    if (
      isPersistedAlphaRiskScore(parsed.riskScore)
    ) draft.riskScore = parsed.riskScore;
    return draft;
  } catch {
    return {};
  }
}

export function updateAlphaRiskProfileDraft(
  userId: string | null,
  field: AlphaRiskProfileField,
  value: string | number
): void {
  if (!userId) return;
  try {
    const draft = loadAlphaRiskProfileDraft(userId);
    window.localStorage.setItem(storageKey(userId), JSON.stringify({
      ...draft,
      [field]: value,
    }));
  } catch {
    // In-memory state remains usable when storage is unavailable.
  }
}

export function removeAlphaRiskProfileDraftField(
  userId: string | null,
  field: AlphaRiskProfileField,
  savedValue: string | number
): void {
  if (!userId) return;
  try {
    const draft = loadAlphaRiskProfileDraft(userId);
    if (draft[field] !== savedValue) return;
    delete draft[field];
    if (Object.keys(draft).length === 0) {
      window.localStorage.removeItem(storageKey(userId));
    } else {
      window.localStorage.setItem(storageKey(userId), JSON.stringify(draft));
    }
  } catch {
    // A later successful save can retry cleanup.
  }
}

export function clearAlphaRiskProfileDraftField(
  userId: string | null,
  field: AlphaRiskProfileField
): void {
  if (!userId) return;
  try {
    const draft = loadAlphaRiskProfileDraft(userId);
    if (!(field in draft)) return;
    delete draft[field];
    if (Object.keys(draft).length === 0) {
      window.localStorage.removeItem(storageKey(userId));
    } else {
      window.localStorage.setItem(storageKey(userId), JSON.stringify(draft));
    }
  } catch {
    // Storage cleanup is best effort.
  }
}

export function clearAlphaRiskProfileDraft(userId: string | null): void {
  if (!userId) return;
  try {
    window.localStorage.removeItem(storageKey(userId));
  } catch {
    // Storage cleanup is best effort.
  }
}
